import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, Loader2, Plus, Trash2, Pencil, User, Save, Camera, Upload, MapPin,
  Phone, BadgeCheck, Search, HeartPulse, History, Check,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ID_TYPES = ["Aadhaar", "PAN", "Voter ID", "Driving License", "Passport", "Other"];
const MARITAL = ["Single", "Married", "Widow", "Widower"];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-", "Unknown"];
const CATEGORIES = ["SC", "ST", "OBC", "General"];

const EMPTY_FORM = {
  id: null,
  ncc_site_name: "",
  name: "",
  father_husband_name: "",
  trade: "",
  mobile: "",
  gender: "",
  blood_group: "",
  date_of_birth: "",
  id_proof_type: "Aadhaar",
  id_proof_number: "",
  pincode: "",
  village: "",
  city: "",
  state: "",
  address: "",
  nationality: "Indian",
  religion: "",
  category: "",
  experience_years: "",
  marital_status: "",
  children_count: "",
  mother_tongue: "",
  other_languages: "",
  emergency_contact_name: "",
  emergency_contact_number: "",
  vision: "",
  height: "",
  weight: "",
  hearing: "",
  physical_deformities: "",
  medical_history: "",
  // Physical Examination — Form XI
  phys_exam_blood_pressure: "",
  phys_exam_pulse: "",
  phys_exam_refractive_error: "",
  phys_exam_colour_vision: "",
  phys_exam_disability: "",
  phys_exam_arm_function: "",
  phys_exam_leg_function: "",
  // Enquiry of Previous History — Form XI
  hist_varicose: "",
  hist_seizure: "",
  hist_vertigo: "",
  hist_acrophobia: "",
  hist_diabetes: "",
  hist_stroke: "",
  hist_heart_diseases: "",
  hist_major_illness_surgery: "",
  hist_symptoms_visible: "",
  hist_others: "",
  photo_base64: "",
};

function computeAge(dob) {
  if (!dob) return "";
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return "";
  const t = new Date();
  let a = t.getFullYear() - d.getFullYear();
  const m = t.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a--;
  return a >= 0 && a < 130 ? String(a) : "";
}

function formatIdNumber(type, raw) {
  if (type === "Aadhaar") {
    const d = (raw || "").replace(/\D/g, "").slice(0, 12);
    return d.replace(/(\d{4})(?=\d)/g, "$1 ");
  }
  if (type === "PAN") {
    return (raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
  }
  return (raw || "").slice(0, 30);
}

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// Compress an image File to <= maxBytes (default 5MB) by iteratively
// reducing JPEG quality + max dimension. Returns a base64 data URL.
async function compressImage(file, maxBytes = 5 * 1024 * 1024) {
  if (file.size <= maxBytes) return await fileToBase64(file);
  const dataUrl = await fileToBase64(file);
  const img = await new Promise((res, rej) => {
    const im = new Image();
    im.onload = () => res(im);
    im.onerror = rej;
    im.src = dataUrl;
  });
  // Start at native dimensions, progressively scale + lower quality until under budget
  let scale = 1;
  let quality = 0.92;
  let out = dataUrl;
  // Each base64 char ~ 0.75 bytes; we want byte-length under maxBytes
  const byteSize = (b64) => Math.floor((b64.length - "data:image/jpeg;base64,".length) * 0.75);
  for (let i = 0; i < 8; i++) {
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff"; // flatten transparency
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    out = canvas.toDataURL("image/jpeg", quality);
    if (byteSize(out) <= maxBytes) return out;
    if (quality > 0.55) quality -= 0.1; else scale *= 0.85;
  }
  return out;
}

export default function WorkmenDrawer({
  open, onClose, mode, contractorId, contractorName, crewSize, authToken, onChanged, editingWorkmanId,
}) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState("");
  // On mobile/tablet (< md breakpoint) we show only one pane at a time so
  // the form gets the full viewport width — typing 20+ Form-XI fields in a
  // 50% column on a phone is unusable. Desktop keeps the side-by-side view.
  const [mobileView, setMobileView] = useState("form"); // "form" | "list"
  // Local-storage draft auto-save — protects a contractor entering 50+
  // Form-XI fields on a flaky 3G connection from losing progress on tab
  // switch, reload, or accidental drawer close.
  // pendingDraft is set on drawer-open when a saved draft exists; the user
  // gets an explicit Resume / Discard prompt instead of silent restore.
  const [pendingDraft, setPendingDraft] = useState(null); // { form, savedAt }
  const [draftSavedAt, setDraftSavedAt] = useState(null); // Date | null
  const [pinLoading, setPinLoading] = useState(false);
  const [pinSuggestions, setPinSuggestions] = useState([]);
  const [nccSites, setNccSites] = useState([]);

  const headers = useMemo(() => ({ Authorization: `Bearer ${authToken}` }), [authToken]);
  const baseUrl = mode === "self"
    ? `${API}/contractor-auth/workmen`
    : `${API}/contractors/${contractorId}/workmen`;

  const updateBaseUrl = (wid) => mode === "self"
    ? `${API}/contractor-auth/workmen/${wid}`
    : `${API}/workmen/${wid}`;

  const deleteUrl = (wid) => mode === "self"
    ? `${API}/contractor-auth/workmen/${wid}`
    : `${API}/workmen/${wid}`;

  // Per-contractor draft key. In self-service mode there's only one logged-in
  // contractor so a single key is enough; in admin mode each contractor gets
  // their own draft slot so admins switching between contractors don't bleed
  // entries across crews.
  const draftKey = useMemo(
    () => `workmen-draft-v1-${mode === "self" ? "self" : (contractorId || "unknown")}`,
    [mode, contractorId],
  );

  // A form is "non-empty" if the user has typed anything beyond the defaults
  // (Aadhaar ID-type and Indian nationality). Used to decide whether a draft
  // is worth saving / restoring.
  function isFormSubstantive(f) {
    if (!f) return false;
    const meaningfulKeys = Object.keys(EMPTY_FORM).filter((k) => k !== "id" && k !== "nationality" && k !== "id_proof_type");
    return meaningfulKeys.some((k) => {
      const v = f[k];
      return v != null && String(v).trim() !== "";
    });
  }

  async function refresh() {
    setLoading(true);
    try {
      const r = await axios.get(baseUrl, { headers });
      setList(r.data || []);
      onChanged?.((r.data || []).length);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load workmen");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) {
      (async () => {
        await refresh();
        // Re-read latest list after refresh; we'll handle auto-edit in a separate effect.
        if (!editingWorkmanId) {
          setForm(EMPTY_FORM);
          setEditing(false);
        }
        setSearch("");
        // Fetch active NCC Sites from masters (public meta endpoint)
        try {
          const m = await axios.get(`${API}/meta/trades`);
          setNccSites(m.data?.ncc_sites || []);
        } catch { /* ignore */ }
        // Look for an unsaved draft from a previous session. We only restore
        // when the drawer opens in "add" mode (no editingWorkmanId) — edits
        // already have a server record and shouldn't be overwritten by a
        // stale draft from a different workman.
        if (!editingWorkmanId && typeof window !== "undefined") {
          try {
            const raw = window.localStorage.getItem(draftKey);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed?.form && isFormSubstantive(parsed.form)) {
                setPendingDraft({ form: parsed.form, savedAt: parsed.savedAt || null });
              } else {
                window.localStorage.removeItem(draftKey);
              }
            }
          } catch { /* corrupt draft — ignore */ }
        }
      })();
    } else {
      // Drawer closed — drop any pending-restore prompt; the draft itself
      // stays in localStorage so the next open can offer it again.
      setPendingDraft(null);
      setDraftSavedAt(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced draft auto-save. Fires ~600ms after the last keystroke when
  // the user is composing a NEW workman (not editing an existing record).
  useEffect(() => {
    if (!open) return;
    if (editing) return; // edits hit the server PUT directly
    if (typeof window === "undefined") return;
    if (!isFormSubstantive(form)) return;
    const t = setTimeout(() => {
      try {
        const payload = { form, savedAt: new Date().toISOString() };
        window.localStorage.setItem(draftKey, JSON.stringify(payload));
        setDraftSavedAt(new Date());
      } catch { /* quota exceeded — fail silently, never block typing */ }
    }, 600);
    return () => clearTimeout(t);
  }, [form, open, editing, draftKey]);

  function clearDraft() {
    try { window.localStorage.removeItem(draftKey); } catch { /* ignore */ }
    setDraftSavedAt(null);
    setPendingDraft(null);
  }

  function resumeDraft() {
    if (!pendingDraft?.form) return;
    setForm({ ...EMPTY_FORM, ...pendingDraft.form });
    setEditing(false);
    setPendingDraft(null);
    setMobileView("form");
    toast.success("Draft restored");
  }

  function discardDraft() {
    clearDraft();
    toast.message("Draft discarded");
  }

  // Auto-open edit form when an editingWorkmanId is provided (e.g., user clicked pencil on inline list).
  useEffect(() => {
    if (!open) return;
    if (!editingWorkmanId) return;
    const w = list.find((x) => x.id === editingWorkmanId);
    if (w) startEdit(w);
  }, [open, editingWorkmanId, list]);

  // Auto-derive "Any Physical Deformities" textarea from the Form-XI Physical Examination sub-fields.
  useEffect(() => {
    const parts = [
      ["BP", form.phys_exam_blood_pressure],
      ["Pulse", form.phys_exam_pulse],
      ["Refractive Error", form.phys_exam_refractive_error],
      ["Colour Vision", form.phys_exam_colour_vision],
      ["Disability", form.phys_exam_disability],
      ["Arm Function & Grip", form.phys_exam_arm_function],
      ["Leg & Foot Function", form.phys_exam_leg_function],
    ].filter(([, v]) => (v || "").trim() !== "")
     .map(([k, v]) => `${k}: ${v.trim()}`);
    const summary = parts.join("; ");
    setForm((f) => f.physical_deformities === summary ? f : { ...f, physical_deformities: summary });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.phys_exam_blood_pressure, form.phys_exam_pulse, form.phys_exam_refractive_error,
    form.phys_exam_colour_vision, form.phys_exam_disability, form.phys_exam_arm_function,
    form.phys_exam_leg_function,
  ]);

  // Auto-derive "Any Medical History" textarea from the Form-XI Enquiry of Previous History sub-fields.
  useEffect(() => {
    const parts = [
      ["Varicose", form.hist_varicose],
      ["Seizure", form.hist_seizure],
      ["Vertigo", form.hist_vertigo],
      ["Acrophobia", form.hist_acrophobia],
      ["Diabetes", form.hist_diabetes],
      ["Stroke", form.hist_stroke],
      ["Heart Diseases", form.hist_heart_diseases],
      ["Major Illness/Surgery", form.hist_major_illness_surgery],
      ["Symptoms Visible", form.hist_symptoms_visible],
      ["Others", form.hist_others],
    ].filter(([, v]) => (v || "").trim() !== "")
     .map(([k, v]) => `${k}: ${v.trim()}`);
    const summary = parts.join("; ");
    setForm((f) => f.medical_history === summary ? f : { ...f, medical_history: summary });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.hist_varicose, form.hist_seizure, form.hist_vertigo, form.hist_acrophobia,
    form.hist_diabetes, form.hist_stroke, form.hist_heart_diseases,
    form.hist_major_illness_surgery, form.hist_symptoms_visible, form.hist_others,
  ]);

  async function onPincodeChange(v) {
    const pin = (v || "").replace(/\D/g, "").slice(0, 6);
    setForm((f) => ({ ...f, pincode: pin }));
    if (pin.length !== 6) {
      setPinSuggestions([]);
      return;
    }
    setPinLoading(true);
    try {
      const r = await axios.get(`${API}/meta/pincode/${pin}`);
      setForm((f) => ({
        ...f,
        pincode: pin,
        city: f.city || r.data.city || "",
        state: f.state || r.data.state || "",
      }));
      setPinSuggestions(Array.isArray(r.data.post_offices) ? r.data.post_offices : []);
    } catch (err) {
      setPinSuggestions([]);
    } finally {
      setPinLoading(false);
    }
  }

  async function onPhotoFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const sizeMB = (f.size / (1024 * 1024)).toFixed(1);
      const b64 = await compressImage(f, 5 * 1024 * 1024);
      const compressedKB = Math.round(b64.length * 0.75 / 1024);
      setForm((prev) => ({ ...prev, photo_base64: b64 }));
      if (f.size > 5 * 1024 * 1024) {
        toast.success(`Photo (${sizeMB} MB) auto-compressed to ~${(compressedKB / 1024).toFixed(1)} MB`);
      }
    } catch {
      toast.error("Could not read or compress photo");
    }
  }

  function startEdit(w) {
    const idType = w.id_proof_type || (w.aadhaar ? "Aadhaar" : "Aadhaar");
    const idNum = w.id_proof_number || (w.aadhaar ? w.aadhaar : "");
    setForm({
      id: w.id,
      ncc_site_name: w.ncc_site_name || "",
      name: w.name || "",
      father_husband_name: w.father_husband_name || "",
      trade: w.trade || "",
      mobile: w.mobile || "",
      gender: w.gender || "",
      blood_group: w.blood_group || "",
      date_of_birth: w.date_of_birth || "",
      id_proof_type: idType,
      id_proof_number: formatIdNumber(idType, idNum),
      pincode: w.pincode || "",
      village: w.village || "",
      city: w.city || "",
      state: w.state || "",
      address: w.address || "",
      nationality: w.nationality || "Indian",
      religion: w.religion || "",
      category: w.category || "",
      experience_years: w.experience_years ?? "",
      marital_status: w.marital_status || "",
      children_count: w.children_count ?? "",
      mother_tongue: w.mother_tongue || "",
      other_languages: w.other_languages || "",
      emergency_contact_name: w.emergency_contact_name || "",
      emergency_contact_number: w.emergency_contact_number || "",
      vision: w.vision || "",
      height: w.height || "",
      weight: w.weight || "",
      hearing: w.hearing || "",
      physical_deformities: w.physical_deformities || "",
      medical_history: w.medical_history || "",
      phys_exam_blood_pressure: w.phys_exam_blood_pressure || "",
      phys_exam_pulse: w.phys_exam_pulse || "",
      phys_exam_refractive_error: w.phys_exam_refractive_error || "",
      phys_exam_colour_vision: w.phys_exam_colour_vision || "",
      phys_exam_disability: w.phys_exam_disability || "",
      phys_exam_arm_function: w.phys_exam_arm_function || "",
      phys_exam_leg_function: w.phys_exam_leg_function || "",
      hist_varicose: w.hist_varicose || "",
      hist_seizure: w.hist_seizure || "",
      hist_vertigo: w.hist_vertigo || "",
      hist_acrophobia: w.hist_acrophobia || "",
      hist_diabetes: w.hist_diabetes || "",
      hist_stroke: w.hist_stroke || "",
      hist_heart_diseases: w.hist_heart_diseases || "",
      hist_major_illness_surgery: w.hist_major_illness_surgery || "",
      hist_symptoms_visible: w.hist_symptoms_visible || "",
      hist_others: w.hist_others || "",
      photo_base64: w.photo_base64 || "",
    });
    setEditing(true);
    // On mobile, the list pane is shown — switch back to the form pane so
    // the user can actually see what they're editing.
    setMobileView("form");
    if (w.pincode && w.pincode.length === 6) {
      axios.get(`${API}/meta/pincode/${w.pincode}`)
        .then((r) => setPinSuggestions(Array.isArray(r.data.post_offices) ? r.data.post_offices : []))
        .catch(() => setPinSuggestions([]));
    } else {
      setPinSuggestions([]);
    }
  }

  function resetForm() {
    setForm(EMPTY_FORM);
    setEditing(false);
    setPinSuggestions([]);
  }

  async function submit(e) {
    e?.preventDefault?.();
    if (!form.name.trim()) {
      toast.error("Name of Person is required");
      return;
    }
    // Validate Aadhaar specifically (other IDs are free-form)
    if (form.id_proof_type === "Aadhaar" && form.id_proof_number) {
      const digits = form.id_proof_number.replace(/\D/g, "");
      if (digits.length !== 12) {
        toast.error("Aadhaar must be 12 digits");
        return;
      }
    }
    setBusy(true);
    try {
      const idDigits = form.id_proof_type === "Aadhaar"
        ? form.id_proof_number.replace(/\D/g, "")
        : null;
      const body = {
        ncc_site_name: form.ncc_site_name || null,
        name: form.name.trim(),
        father_husband_name: form.father_husband_name.trim() || null,
        trade: form.trade.trim() || null,
        mobile: form.mobile.trim() || null,
        gender: form.gender || null,
        blood_group: form.blood_group || null,
        date_of_birth: form.date_of_birth || null,
        id_proof_type: form.id_proof_type || null,
        id_proof_number: form.id_proof_number.trim() || null,
        aadhaar: idDigits || null,
        pincode: form.pincode || null,
        village: form.village.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        address: form.address.trim() || null,
        nationality: form.nationality.trim() || null,
        religion: form.religion.trim() || null,
        category: form.category || null,
        experience_years: form.experience_years === "" ? 0 : Number(form.experience_years),
        marital_status: form.marital_status || null,
        children_count: form.children_count === "" ? null : Number(form.children_count),
        mother_tongue: form.mother_tongue.trim() || null,
        other_languages: form.other_languages.trim() || null,
        emergency_contact_name: form.emergency_contact_name.trim() || null,
        emergency_contact_number: form.emergency_contact_number.trim() || null,
        vision: form.vision.trim() || null,
        height: form.height.trim() || null,
        weight: form.weight.trim() || null,
        hearing: form.hearing.trim() || null,
        physical_deformities: form.physical_deformities.trim() || null,
        medical_history: form.medical_history.trim() || null,
        phys_exam_blood_pressure: form.phys_exam_blood_pressure.trim() || null,
        phys_exam_pulse: form.phys_exam_pulse.trim() || null,
        phys_exam_refractive_error: form.phys_exam_refractive_error.trim() || null,
        phys_exam_colour_vision: form.phys_exam_colour_vision.trim() || null,
        phys_exam_disability: form.phys_exam_disability.trim() || null,
        phys_exam_arm_function: form.phys_exam_arm_function.trim() || null,
        phys_exam_leg_function: form.phys_exam_leg_function.trim() || null,
        hist_varicose: form.hist_varicose.trim() || null,
        hist_seizure: form.hist_seizure.trim() || null,
        hist_vertigo: form.hist_vertigo.trim() || null,
        hist_acrophobia: form.hist_acrophobia.trim() || null,
        hist_diabetes: form.hist_diabetes.trim() || null,
        hist_stroke: form.hist_stroke.trim() || null,
        hist_heart_diseases: form.hist_heart_diseases.trim() || null,
        hist_major_illness_surgery: form.hist_major_illness_surgery.trim() || null,
        hist_symptoms_visible: form.hist_symptoms_visible.trim() || null,
        hist_others: form.hist_others.trim() || null,
        photo_base64: form.photo_base64 || null,
      };
      if (editing && form.id) {
        await axios.put(updateBaseUrl(form.id), body, { headers });
        toast.success("Workman updated");
      } else {
        await axios.post(baseUrl, body, { headers });
        toast.success("Workman registered");
      }
      resetForm();
      await refresh();
      // After a successful add (not an edit) on mobile, flip to the list
      // so the contractor sees their newly-registered worker appear.
      if (!editing) {
        clearDraft();
        setMobileView("list");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove(w) {
    if (!window.confirm(`Delete workman "${w.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(deleteUrl(w.id), { headers });
      toast.success("Workman deleted");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((w) =>
      [w.name, w.trade, w.mobile, w.aadhaar, w.id_proof_number, w.city, w.state, w.village]
        .filter(Boolean).join(" ").toLowerCase().includes(q)
    );
  }, [list, search]);

  if (!open) return null;

  const balance = (Number(crewSize) || 0) - list.length;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/40" onClick={onClose}>
      <aside
        className="flex h-full w-full max-w-3xl flex-col border-l-4 border-ncc-gold bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="workmen-drawer"
      >
        <header className="flex items-start justify-between border-b-2 border-ncc-navy bg-ncc-navy px-5 py-4 text-white">
          <div>
            <div className="industrial-label text-ncc-gold2">// Workmen Roster</div>
            <h2 className="mt-1 font-heading text-xl font-extrabold tracking-tight">
              {contractorName ? contractorName : "Register Workmen / Labour"}
            </h2>
            <div className="mt-1 font-mono text-xs text-white/80">
              <span className="rounded bg-white/10 px-2 py-0.5">Registered: <b>{list.length}</b></span>
              {Number.isFinite(Number(crewSize)) && crewSize > 0 && (
                <>
                  <span className="mx-2">·</span>
                  <span className="rounded bg-white/10 px-2 py-0.5">Crew Target: <b>{crewSize}</b></span>
                  <span className="mx-2">·</span>
                  <span className={`rounded px-2 py-0.5 ${balance > 0 ? "bg-amber-400/30" : balance < 0 ? "bg-red-500/30" : "bg-emerald-400/30"}`}>
                    Balance: <b>{balance}</b>
                  </span>
                </>
              )}
            </div>
          </div>
          <button onClick={onClose} data-testid="workmen-drawer-close" className="rounded-md p-1 hover:bg-white/10">
            <X className="h-5 w-5" />
          </button>
        </header>

        {/* Mobile-only segmented toggle — hidden on md+ where both panes show */}
        <div className="flex border-b-2 border-slate-900 bg-white md:hidden" data-testid="workmen-mobile-toggle">
          <button
            type="button"
            onClick={() => setMobileView("form")}
            data-testid="workmen-mobile-toggle-form"
            className={`flex-1 px-4 py-2.5 industrial-label text-sm transition-colors ${
              mobileView === "form" ? "bg-ncc-navy text-white" : "bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            {editing ? "Edit Workman" : "Add Workman"}
          </button>
          <button
            type="button"
            onClick={() => setMobileView("list")}
            data-testid="workmen-mobile-toggle-list"
            className={`flex-1 border-l-2 border-slate-900 px-4 py-2.5 industrial-label text-sm transition-colors ${
              mobileView === "list" ? "bg-ncc-navy text-white" : "bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            Registered ({list.length})
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Form */}
          <form onSubmit={submit} className={`w-full overflow-y-auto border-r border-slate-200 bg-slate-50 p-4 md:w-1/2 md:block ${mobileView === "form" ? "block" : "hidden"}`} data-testid="workmen-form">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="industrial-label text-ncc-navy">
                {editing ? "Edit Workman" : "Add New Workman"}
              </div>
              <div className="flex items-center gap-2">
                {/* Draft auto-save indicator — only meaningful for new entries */}
                {!editing && draftSavedAt && (
                  <span
                    title={`Draft auto-saved at ${draftSavedAt.toLocaleTimeString()}. Survives reload, tab switch, and accidental drawer close.`}
                    data-testid="workmen-draft-indicator"
                    className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 font-mono text-[10px] text-emerald-700"
                  >
                    <Check className="h-3 w-3" /> Draft saved
                  </span>
                )}
                {editing && (
                  <button type="button" onClick={resetForm} className="text-xs font-bold text-slate-500 underline">Cancel edit</button>
                )}
              </div>
            </div>

            {/* Restore-draft banner — appears when an unsaved draft from a
                previous session is detected. The user must explicitly choose
                Resume or Discard — we never silently overwrite their next
                keystrokes. */}
            {!editing && pendingDraft && (
              <div
                className="mb-3 flex items-start gap-2 rounded-md border-2 border-amber-400 bg-amber-50 p-3"
                data-testid="workmen-draft-banner"
              >
                <History className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-amber-900 text-sm">Unsaved draft from {pendingDraft.savedAt ? new Date(pendingDraft.savedAt).toLocaleString() : "a previous session"}</div>
                  <div className="mt-0.5 font-mono text-[11px] text-amber-800">
                    {pendingDraft.form?.name?.trim() ? <>For: <b>{pendingDraft.form.name}</b></> : "No name yet — partial entry."}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={resumeDraft}
                      data-testid="workmen-draft-resume"
                      className="inline-flex items-center gap-1 rounded border-2 border-amber-700 bg-amber-700 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-amber-800"
                    >
                      <History className="h-3 w-3" /> Resume
                    </button>
                    <button
                      type="button"
                      onClick={discardDraft}
                      data-testid="workmen-draft-discard"
                      className="inline-flex items-center gap-1 rounded border-2 border-amber-300 bg-white px-2.5 py-1 text-[11px] font-bold text-amber-800 hover:bg-amber-50"
                    >
                      <X className="h-3 w-3" /> Discard
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              {/* GROUP 1 BANNER · Personal Information */}
              <div className="col-span-2 rounded-md border-l-4 border-ncc-navy bg-slate-100 px-3 py-2" data-testid="workmen-group-personal">
                <div className="industrial-label text-[10px] text-ncc-navy">// Personal Information</div>
              </div>

              {/* PROJECT / NCC SITE */}
              <Field label="Name of Project" full>
                <select
                  value={form.ncc_site_name}
                  onChange={(e) => setForm({ ...form, ncc_site_name: e.target.value })}
                  data-testid="workmen-ncc-site"
                  className={inputCls}
                >
                  <option value="">— Select NCC Site / Project —</option>
                  {nccSites.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {nccSites.length === 0 && (
                  <div className="mt-1 font-mono text-[10px] text-amber-700">
                    No active NCC Sites in masters. Add one in Admin → Settings → Masters → NCC Sites.
                  </div>
                )}
              </Field>

              {/* PHOTO */}
              <Field label="Photo" full>
                <div className="flex items-center gap-3">
                  <div className="grid h-16 w-16 place-items-center overflow-hidden rounded-full border-2 border-ncc-gold/40 bg-white">
                    {form.photo_base64 ? (
                      <img src={form.photo_base64} alt="" className="h-full w-full object-cover" />
                    ) : <User className="h-7 w-7 text-slate-400" />}
                  </div>
                  <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border-2 border-ncc-navy bg-white px-2.5 py-1.5 industrial-label text-ncc-navy hover:bg-slate-50">
                    <Camera className="h-3.5 w-3.5" />
                    Camera
                    <input type="file" accept="image/*" capture="environment" onChange={onPhotoFile} className="hidden" data-testid="workmen-photo-camera" />
                  </label>
                  <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border-2 border-ncc-navy bg-white px-2.5 py-1.5 industrial-label text-ncc-navy hover:bg-slate-50">
                    <Upload className="h-3.5 w-3.5" />
                    Upload
                    <input type="file" accept="image/*" onChange={onPhotoFile} className="hidden" data-testid="workmen-photo-upload" />
                  </label>
                  {form.photo_base64 && (
                    <button type="button" onClick={() => setForm((f) => ({ ...f, photo_base64: "" }))} className="text-xs text-red-600 underline">Remove</button>
                  )}
                </div>
              </Field>

              {/* IDENTITY */}
              <Field label="Name of Person" required full>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="workmen-name" className={inputCls} />
              </Field>

              <Field label="Father's / Husband's Name" full>
                <input value={form.father_husband_name} onChange={(e) => setForm({ ...form, father_husband_name: e.target.value })} placeholder="e.g. Ramesh Kumar" data-testid="workmen-father-husband" className={inputCls} />
              </Field>

              <Field label="Date of Birth">
                <input type="date" max={new Date().toISOString().slice(0, 10)} value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="workmen-dob" className={inputCls} />
              </Field>
              <Field label="Age (Yrs)">
                <input readOnly value={computeAge(form.date_of_birth)} placeholder="auto" className={`${inputCls} cursor-not-allowed bg-slate-100`} data-testid="workmen-age" />
              </Field>

              <Field label="Mobile">
                <input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 13) })} placeholder="10 digits" data-testid="workmen-mobile" className={inputCls} />
              </Field>
              <Field label="Gender">
                <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} data-testid="workmen-gender" className={inputCls}>
                  <option value="">—</option>
                  <option>Male</option><option>Female</option><option>Other</option>
                </select>
              </Field>

              {/* ID PROOF */}
              <Field label="ID Proof Type">
                <select
                  value={form.id_proof_type}
                  onChange={(e) => setForm({ ...form, id_proof_type: e.target.value, id_proof_number: "" })}
                  data-testid="workmen-id-type"
                  className={inputCls}
                >
                  {ID_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label={`${form.id_proof_type || "ID"} Number`}>
                <input
                  value={form.id_proof_number}
                  onChange={(e) => setForm({ ...form, id_proof_number: formatIdNumber(form.id_proof_type, e.target.value) })}
                  placeholder={form.id_proof_type === "Aadhaar" ? "XXXX XXXX XXXX" : form.id_proof_type === "PAN" ? "ABCDE1234F" : "ID Number"}
                  data-testid="workmen-id-number"
                  className={inputCls}
                />
              </Field>

              {/* ADDRESS */}
              <Field label="Address" full>
                <textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="workmen-address" placeholder="House / Street / Landmark" className={`${inputCls} font-sans`} />
              </Field>

              <Field label="Pincode">
                <div className="relative">
                  <input
                    value={form.pincode}
                    onChange={(e) => onPincodeChange(e.target.value)}
                    placeholder="6 digits"
                    data-testid="workmen-pincode"
                    className={inputCls}
                  />
                  {pinLoading && <Loader2 className="absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-slate-400" />}
                </div>
              </Field>
              <Field label="Village">
                <input
                  list="workmen-village-suggestions"
                  value={form.village}
                  onChange={(e) => setForm({ ...form, village: e.target.value })}
                  placeholder={pinSuggestions.length ? "Pick or type…" : ""}
                  data-testid="workmen-village"
                  className={inputCls}
                />
                <datalist id="workmen-village-suggestions">
                  {pinSuggestions.map((p) => <option key={p} value={p} />)}
                </datalist>
              </Field>
              <Field label="City / District">
                <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="workmen-city" className={inputCls} />
              </Field>
              <Field label="State">
                <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="workmen-state" className={inputCls} />
              </Field>

              <Field label="Blood Group">
                <select value={form.blood_group} onChange={(e) => setForm({ ...form, blood_group: e.target.value })} data-testid="workmen-blood-group" className={inputCls}>
                  <option value="">—</option>
                  {BLOOD_GROUPS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
              <Field label="Nationality">
                <input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} placeholder="Indian" data-testid="workmen-nationality" className={inputCls} />
              </Field>

              <Field label="Religion (Optional)">
                <input value={form.religion} onChange={(e) => setForm({ ...form, religion: e.target.value })} placeholder="e.g. Hindu / Muslim / Christian" data-testid="workmen-religion" className={inputCls} />
              </Field>
              <Field label="Category (Optional)">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="workmen-category" className={inputCls}>
                  <option value="">—</option>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>

              <Field label="Trade">
                <input value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value })} placeholder="e.g. Mason" data-testid="workmen-trade" className={inputCls} />
              </Field>
              <Field label="Experience (Yrs)">
                <input type="number" min="0" value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })} data-testid="workmen-exp" className={inputCls} />
              </Field>

              <Field label="Marital Status">
                <select value={form.marital_status} onChange={(e) => setForm({ ...form, marital_status: e.target.value })} data-testid="workmen-marital" className={inputCls}>
                  <option value="">—</option>
                  {MARITAL.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
              <Field label="No. of Children">
                <input type="number" min="0" max="20" value={form.children_count} onChange={(e) => setForm({ ...form, children_count: e.target.value })} data-testid="workmen-children" className={inputCls} />
              </Field>

              <Field label="Mother Tongue">
                <input value={form.mother_tongue} onChange={(e) => setForm({ ...form, mother_tongue: e.target.value })} placeholder="e.g. Hindi" data-testid="workmen-mother-tongue" className={inputCls} />
              </Field>
              <Field label="Other Languages Known">
                <input value={form.other_languages} onChange={(e) => setForm({ ...form, other_languages: e.target.value })} placeholder="e.g. English, Tamil" data-testid="workmen-other-languages" className={inputCls} />
              </Field>

              <Field label="Emergency Contact Name">
                <input value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} placeholder="e.g. Wife / Father" data-testid="workmen-emerg-name" className={inputCls} />
              </Field>
              <Field label="Emergency Contact Number">
                <input value={form.emergency_contact_number} onChange={(e) => setForm({ ...form, emergency_contact_number: e.target.value.replace(/[^\d+\s-]/g, "").slice(0, 15) })} placeholder="10 digits" data-testid="workmen-emerg-number" className={inputCls} />
              </Field>

              {/* GROUP 2 BANNER · Physical Condition */}
              <div className="col-span-2 mt-1 flex items-center gap-2 rounded-md border-l-4 border-emerald-500 bg-emerald-50 px-3 py-2" data-testid="workmen-group-physical">
                <HeartPulse className="h-3.5 w-3.5 text-emerald-700" />
                <div className="industrial-label text-[10px] text-emerald-900">// Physical Condition</div>
              </div>
              <Field label="Vision">
                <input value={form.vision} onChange={(e) => setForm({ ...form, vision: e.target.value })} placeholder="e.g. 6/6, Specs" data-testid="workmen-vision" className={inputCls} />
              </Field>
              <Field label="Hearing">
                <input value={form.hearing} onChange={(e) => setForm({ ...form, hearing: e.target.value })} placeholder="Normal / Impaired" data-testid="workmen-hearing" className={inputCls} />
              </Field>
              <Field label="Height">
                <input value={form.height} onChange={(e) => setForm({ ...form, height: e.target.value })} placeholder="e.g. 5'8&quot; / 173 cm" data-testid="workmen-height" className={inputCls} />
              </Field>
              <Field label="Weight">
                <input value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} placeholder="e.g. 70 kg" data-testid="workmen-weight" className={inputCls} />
              </Field>

              {/* GROUP 3 BANNER · Medical Condition */}
              <div className="col-span-2 mt-1 flex items-center gap-2 rounded-md border-l-4 border-emerald-600 bg-emerald-50 px-3 py-2" data-testid="workmen-group-medical">
                <HeartPulse className="h-3.5 w-3.5 text-emerald-700" />
                <div className="industrial-label text-[10px] text-emerald-900">// Medical Condition</div>
              </div>
              <Field label="Any Physical Deformities" full>
                <textarea
                  rows={2}
                  readOnly
                  value={form.physical_deformities}
                  placeholder="Auto-filled from Physical Examination below…"
                  data-testid="workmen-deformities"
                  className={`${inputCls} cursor-not-allowed bg-slate-100 font-sans`}
                />
                <div className="mt-0.5 font-mono text-[10px] text-slate-500">Auto-summary of Physical Examination fields below.</div>
              </Field>
              <Field label="Any Medical History" full>
                <textarea
                  rows={2}
                  readOnly
                  value={form.medical_history}
                  placeholder="Auto-filled from Enquiry of Previous History below…"
                  data-testid="workmen-medical"
                  className={`${inputCls} cursor-not-allowed bg-slate-100 font-sans`}
                />
                <div className="mt-0.5 font-mono text-[10px] text-slate-500">Auto-summary of Enquiry of Previous History fields below.</div>
              </Field>

              {/* PHYSICAL EXAMINATION (Form XI) */}
              <div className="col-span-2 mt-1 rounded-md border border-emerald-200 bg-emerald-50/40 px-3 py-1.5" data-testid="workmen-subgroup-phys-exam">
                <div className="industrial-label text-[10px] text-emerald-800">// Physical Examination · Form-XI</div>
              </div>
              <Field label="Blood Pressure">
                <input value={form.phys_exam_blood_pressure} onChange={(e) => setForm({ ...form, phys_exam_blood_pressure: e.target.value })} placeholder="e.g. 120/80 mmHg" data-testid="workmen-pe-bp" className={inputCls} />
              </Field>
              <Field label="Pulse">
                <input value={form.phys_exam_pulse} onChange={(e) => setForm({ ...form, phys_exam_pulse: e.target.value })} placeholder="e.g. 72 bpm" data-testid="workmen-pe-pulse" className={inputCls} />
              </Field>
              <Field label="Refractive Error">
                <input value={form.phys_exam_refractive_error} onChange={(e) => setForm({ ...form, phys_exam_refractive_error: e.target.value })} placeholder="None / describe" data-testid="workmen-pe-refractive" className={inputCls} />
              </Field>
              <Field label="Colour Vision">
                <input value={form.phys_exam_colour_vision} onChange={(e) => setForm({ ...form, phys_exam_colour_vision: e.target.value })} placeholder="Normal / Defective" data-testid="workmen-pe-colour" className={inputCls} />
              </Field>
              <Field label="Any Disability">
                <input value={form.phys_exam_disability} onChange={(e) => setForm({ ...form, phys_exam_disability: e.target.value })} placeholder="None / describe" data-testid="workmen-pe-disability" className={inputCls} />
              </Field>
              <Field label="Arm Function & Grip">
                <input value={form.phys_exam_arm_function} onChange={(e) => setForm({ ...form, phys_exam_arm_function: e.target.value })} placeholder="Normal / Weak / describe" data-testid="workmen-pe-arm" className={inputCls} />
              </Field>
              <Field label="Leg & Foot Function" full>
                <input value={form.phys_exam_leg_function} onChange={(e) => setForm({ ...form, phys_exam_leg_function: e.target.value })} placeholder="Normal / Limp / describe" data-testid="workmen-pe-leg" className={inputCls} />
              </Field>

              {/* ENQUIRY OF PREVIOUS HISTORY (Form XI) */}
              <div className="col-span-2 mt-1 rounded-md border border-emerald-200 bg-emerald-50/40 px-3 py-1.5" data-testid="workmen-subgroup-hist">
                <div className="industrial-label text-[10px] text-emerald-800">// Enquiry of Previous History · Form-XI</div>
              </div>
              <Field label="Varicose">
                <input value={form.hist_varicose} onChange={(e) => setForm({ ...form, hist_varicose: e.target.value })} placeholder="No / Yes" data-testid="workmen-hist-varicose" className={inputCls} />
              </Field>
              <Field label="Seizure">
                <input value={form.hist_seizure} onChange={(e) => setForm({ ...form, hist_seizure: e.target.value })} placeholder="No / Yes" data-testid="workmen-hist-seizure" className={inputCls} />
              </Field>
              <Field label="Vertigo">
                <input value={form.hist_vertigo} onChange={(e) => setForm({ ...form, hist_vertigo: e.target.value })} placeholder="No / Yes" data-testid="workmen-hist-vertigo" className={inputCls} />
              </Field>
              <Field label="Acrophobia">
                <input value={form.hist_acrophobia} onChange={(e) => setForm({ ...form, hist_acrophobia: e.target.value })} placeholder="No / Yes" data-testid="workmen-hist-acrophobia" className={inputCls} />
              </Field>
              <Field label="Diabetes">
                <input value={form.hist_diabetes} onChange={(e) => setForm({ ...form, hist_diabetes: e.target.value })} placeholder="No / Yes" data-testid="workmen-hist-diabetes" className={inputCls} />
              </Field>
              <Field label="Stroke">
                <input value={form.hist_stroke} onChange={(e) => setForm({ ...form, hist_stroke: e.target.value })} placeholder="No / Yes" data-testid="workmen-hist-stroke" className={inputCls} />
              </Field>
              <Field label="Heart Diseases" full>
                <input value={form.hist_heart_diseases} onChange={(e) => setForm({ ...form, hist_heart_diseases: e.target.value })} placeholder="No / Yes / describe" data-testid="workmen-hist-heart" className={inputCls} />
              </Field>
              <Field label="Major Illness or Surgery" full>
                <input value={form.hist_major_illness_surgery} onChange={(e) => setForm({ ...form, hist_major_illness_surgery: e.target.value })} placeholder="None / describe" data-testid="workmen-hist-major" className={inputCls} />
              </Field>
              <Field label="Symptoms Visible" full>
                <input value={form.hist_symptoms_visible} onChange={(e) => setForm({ ...form, hist_symptoms_visible: e.target.value })} placeholder="None / describe" data-testid="workmen-hist-symptoms" className={inputCls} />
              </Field>
              <Field label="Others, if any" full>
                <input value={form.hist_others} onChange={(e) => setForm({ ...form, hist_others: e.target.value })} placeholder="Describe…" data-testid="workmen-hist-others" className={inputCls} />
              </Field>
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="workmen-submit"
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md border-2 border-ncc-navy bg-ncc-gold px-4 py-2.5 industrial-label text-ncc-navy hover:-translate-y-0.5 hover:shadow-brutal-sm disabled:opacity-70"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />)}
              {busy ? "Saving…" : (editing ? "Save Changes" : "Register Workman")}
            </button>
          </form>

          {/* List */}
          <div className={`w-full flex-col overflow-hidden bg-white md:w-1/2 md:flex ${mobileView === "list" ? "flex" : "hidden md:flex"}`}>
            <div className="border-b border-slate-200 p-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, trade, ID, city…"
                  data-testid="workmen-search"
                  className="w-full rounded-md border border-slate-300 bg-slate-50 pl-8 pr-3 py-1.5 font-mono text-xs focus:border-ncc-navy focus:outline-none"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3" data-testid="workmen-list">
              {loading && <div className="grid place-items-center py-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>}
              {!loading && filtered.length === 0 && (
                <div className="grid place-items-center px-4 py-12 text-center text-xs text-slate-500">
                  {list.length === 0 ? "No workmen registered yet. Use the form on the left to add the first one." : "No matches for your search."}
                </div>
              )}
              <ul className="space-y-2">
                {filtered.map((w) => (
                  <li
                    key={w.id}
                    data-testid={`workmen-card-${w.id}`}
                    className="group flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 hover:border-ncc-navy hover:shadow-sm"
                  >
                    <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-full border-2 border-ncc-gold/40 bg-slate-100">
                      {w.photo_base64 ? (
                        <img src={w.photo_base64} alt="" className="h-full w-full object-cover" />
                      ) : <User className="h-5 w-5 text-slate-400" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="truncate font-bold text-slate-900">{w.name}</div>
                        {(w.id_proof_type && w.id_proof_number) ? (
                          <span title={`${w.id_proof_type}: ${w.id_proof_number}`} className="inline-flex items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">
                            <BadgeCheck className="h-3 w-3" /> {w.id_proof_type}
                          </span>
                        ) : w.aadhaar && (
                          <span title={w.aadhaar} className="inline-flex items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">
                            <BadgeCheck className="h-3 w-3" /> Aadhaar
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-[11px] text-slate-600">
                        {w.trade && <span className="rounded bg-slate-100 px-1.5 py-0.5">{w.trade}</span>}
                        {w.age_years != null && <span>{w.age_years} yrs</span>}
                        {w.experience_years ? <span>{w.experience_years}y exp</span> : null}
                        {w.mobile && <span className="inline-flex items-center gap-0.5"><Phone className="h-3 w-3" />{w.mobile}</span>}
                        {(w.city || w.state || w.village) && <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{[w.village, w.city, w.state].filter(Boolean).join(", ")}</span>}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <button
                        type="button"
                        onClick={() => startEdit(w)}
                        data-testid={`workmen-edit-${w.id}`}
                        className="rounded-md border border-slate-300 bg-white p-1.5 text-slate-700 hover:border-ncc-navy hover:text-ncc-navy"
                        title="Edit"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(w)}
                        data-testid={`workmen-delete-${w.id}`}
                        className="rounded-md border border-red-200 bg-white p-1.5 text-red-600 hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

const inputCls = "w-full rounded-md border-2 border-slate-300 bg-white px-2.5 py-1.5 font-mono text-xs focus:border-ncc-navy focus:outline-none";

function Field({ label, required, children, full }) {
  return (
    <label className={`block ${full ? "col-span-2" : ""}`}>
      <div className="industrial-label mb-1 text-[10px] text-slate-500">
        {label}{required && <span className="text-ncc-gold2"> *</span>}
      </div>
      {children}
    </label>
  );
}
